import { ServerDomainInfo, Volume } from '@/api/type-server'
import { NumberInput, Trans } from '@/components'
import { formatDomain } from '@/utils/formatter'
import { formatStorageGB, toBytes } from '@/utils/storage'
import { cn } from '@/utils/style'

import { SelectedData } from './volume-quota-modal'
import { getDatadriveGradientClass } from './volume-usage-segmented-bar'

interface VolumeQuotaSection {
  volumes: Volume[]
  availableInGB: number
  summarizedData: SelectedData
  domainInfos?: ServerDomainInfo[]
  onChange: (volume: Volume, value: number) => void
  isOrderExpired?: boolean
  useGradientChips?: boolean
}

export default function DriveVolumeQuotaSection({
  volumes = [],
  availableInGB,
  summarizedData,
  domainInfos,
  onChange,
  isOrderExpired,
  useGradientChips,
}: VolumeQuotaSection) {
  // If there are no domainInfos or volumes at all, no display
  if (!domainInfos || domainInfos.length === 0) {
    return null
  }

  return (
    <div className="">
      <h3 className="text-primary mb-4">
        <Trans i18nKey="space.volumeItemTitleCloudStorage">Datadrive</Trans>
      </h3>

      <div className="flex flex-col gap-y-4">
        {domainInfos.map((domainInfo, index) => {
          // Find the volume corresponding to this domain
          const volume = volumes.find((vol) => vol.domain === domainInfo.domain)

          const usageMB = volume?.usage_mb || 0
          const currentQuotaGB = volume?.quota_gb || 0
          const usedGB = Number((usageMB / 1024).toFixed(2))
          const usageForDisplay = formatStorageGB(volume?.usage_bytes ?? toBytes(usageMB, 'mb'))

          // Try to find existing selectedData for this domain
          const item = summarizedData.selectedData.find((val) =>
            volume ? val.volume.id === volume.id : val.volume.domain === domainInfo.domain,
          )

          let selectedQuotaInGB: number
          let targetVolume = volume
          if (!item) {
            if (!targetVolume) {
              targetVolume = {
                id: -1 * (domainInfo.domain + 1),
                domain: domainInfo.domain,
                quota_gb: 0,
                usage_mb: 0,
                type: 1,
                init_quota_gb: 0,
                last_quota_gb: 0,
              }
              selectedQuotaInGB = 0
            } else {
              // If a volume exists but no selectedData found, use its current quota
              selectedQuotaInGB = currentQuotaGB
            }
          } else {
            selectedQuotaInGB = item.selectedQuotaInGB
            targetVolume = item.volume
          }

          const quotaMax = selectedQuotaInGB + availableInGB
          const insufficientSpace =
            summarizedData.lastestVolume &&
            targetVolume &&
            summarizedData.lastestVolume.id === targetVolume.id &&
            availableInGB < 0

          // Check if this specific volume has insufficient quota (quota < used space)
          // Use raw bytes to detect tiny usage that rounds to 0 GB
          const rawUsageBytes = volume?.usage_bytes ?? toBytes(usageMB, 'mb')
          const hasInsufficientQuota = rawUsageBytes > 0 && selectedQuotaInGB < Math.max(usedGB, 1)

          const initialQuotaGB = volume
            ? isOrderExpired
              ? volume.last_quota_gb || 0
              : volume.quota_gb || 0
            : 0
          const isZeroingOut = initialQuotaGB > 0 && selectedQuotaInGB === 0

          return (
            <div key={`quota.domain.${domainInfo.domain}.${index}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center justify-betweenj gap-3">
                  <div className="w-[120px]">
                    <div className="flex items-baseline t-sm text-secondary">
                      <div
                        className={cn(
                          'w-3 h-3 mr-2',
                          useGradientChips
                            ? getDatadriveGradientClass(index, isOrderExpired)
                            : 'bg-btn-secondary',
                        )}
                      />
                      {domainInfo.displayName || formatDomain(domainInfo.domain)}
                    </div>
                  </div>
                  <NumberInput
                    value={selectedQuotaInGB}
                    onChange={(value) => {
                      if (targetVolume) {
                        // Call onChange to let parent update summarizedData properly
                        onChange(targetVolume, value)
                      }
                    }}
                    min={isOrderExpired ? 1 : 0}
                    max={quotaMax}
                    step={1}
                    className="w-[160px]"
                    disabled={quotaMax < 1}
                  />
                  <div className="t-sm text-secondary">GB</div>
                </div>
              </div>
              {insufficientSpace && (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans i18nKey="space.volumeItemSubTitleInsufficient">
                    Insufficient available space
                  </Trans>
                </div>
              )}
              {isZeroingOut ? (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans i18nKey="space.datadriveClearWarning">
                    This action will permanently delete all data stored in this Datadrive.
                  </Trans>
                </div>
              ) : (
                hasInsufficientQuota && (
                  <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                    <Trans
                      i18nKey="space.volumeClearWarning"
                      values={{ newQuota: selectedQuotaInGB, usedSpace: usageForDisplay }}
                    />
                  </div>
                )
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
